import type { MembershipRole } from "@safeplate/contracts";

import type { StaffProfile } from "../setup/setup-api.js";

export type PersonTone = "attention" | "neutral" | "positive";

export function roleLabel(role: MembershipRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    day: "numeric",
    month: "short",
    timeZone: "Pacific/Auckland",
  }).format(new Date(iso));
}

type RowInput = Pick<
  StaffProfile,
  | "email"
  | "invitation"
  | "membership"
  | "quick_pin_enabled"
  | "status"
  | "user_id"
>;

export interface PersonRow {
  // One short status, only when something needs doing or the person is inactive.
  chip: { label: string; tone: PersonTone } | null;
  // Primary invitation button shown on the row.
  invite: "invite" | "send_again" | null;
  // Invitation actions that live in the edit dialog.
  pendingInvitation: boolean;
  pinLabel: "Change PIN" | "Set PIN";
  // The row shows Set PIN only while the person has none.
  showSetPin: boolean;
  // Whether a PIN can be set at all.
  canSetPin: boolean;
}

export function personRow(person: RowInput, now: Date = new Date()): PersonRow {
  const inactive = person.status === "inactive";
  const canSetPin = !inactive && Boolean(person.user_id || person.email);
  const pinLabel = person.quick_pin_enabled ? "Change PIN" : "Set PIN";
  const base = {
    canSetPin,
    pinLabel,
    showSetPin: canSetPin && !person.quick_pin_enabled,
  } as const;
  if (inactive) {
    return {
      ...base,
      chip: { label: "Inactive", tone: "neutral" },
      invite: null,
      pendingInvitation: false,
    };
  }
  if (person.user_id) {
    return { ...base, chip: null, invite: null, pendingInvitation: false };
  }
  const invitation = person.invitation;
  if (invitation) {
    const expired =
      invitation.status === "expired" ||
      new Date(invitation.expires_at).getTime() <= now.getTime();
    return expired
      ? {
          ...base,
          chip: { label: "Invite expired", tone: "attention" },
          invite: "send_again",
          pendingInvitation: false,
        }
      : {
          ...base,
          chip: { label: "Invited", tone: "neutral" },
          invite: null,
          pendingInvitation: true,
        };
  }
  return {
    ...base,
    chip: null,
    invite: person.email ? "invite" : null,
    pendingInvitation: false,
  };
}
